import {
  PaymentStatusOption,
  PaymentLifecycleView,
} from "helpers/paymentLifecycle";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import type { ManualKycStatus } from "types/kycStatus";
import type { CompensationItem } from "store/payments/compensations/slice";
import type { ConfiscationItem } from "store/payments/confiscations/slice";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";

export interface LoyaltyPoints {
  id: number;
  type: string;
  points: number;
  created: string;
}
export interface UserCashbackItem {
  id: number;
  title: string;
  level: number;
  type: string;
  image: string;
  min: number;
  max: number;
  percentage: number;
  currency: string;
  netLosses: number;
  status: string;
  calculatedDate: string;
  cashback: number;
}
export interface UserRestrictions {
  dailyDepositLimit: number;
  dailyWithdrawalLimit: number;
  weeklyDepositLimit: number;
  weeklyWithdrawalLimit: number;
  monthlyDepositLimit: number;
  monthlyWithdrawalLimit: number;
  yearlyDepositLimit: number;
  yearlyWithdrawalLimit: number;
  dailyMaxBetSum: number;
  dailyMaxBetCount: number;
  dailyMaxLossSum: number;
}
export interface PlayerLoyaltyReward {
  id?: number;
  type: string;
  title?: string;
  description?: string;
  image?: string | null;
  amount?: string;
  isAvailable?: boolean;
}
export interface LoyaltyProgress {
  level: {
    loyaltyId: number;
    loyaltyLevel: number;
    title: string;
    description: string;
    image: string;
    pointsToComplete: number;
    currentPoints: number;
    valueFrom?: number;
    pointsRequired?: number;
    nextLoyaltyLevel?: number;
    nextPointsToComplete?: number;
    nextTitle?: string;
    nextImage?: string | null;
    rewards?: PlayerLoyaltyReward[];
  };
  points: {
    items: LoyaltyPoints[];
    totalItems: number;
    totalPages: number;
  };
}
export interface UserBonus {
  userBonusId: number;
  claimingEndInMilliseconds: number | null;
  activationEndInMilliseconds: number | null;
  wageringEndInMilliseconds: number | null;
  status: string;
  claimedAt: string;
  totalWagerAmount: number;
  bonusId: number;
  title: string;
  description: string;
  image: string;
  rewards: {
    currency: string;
    type: string;
    config: {
      gameId?: string;
      spinAmount?: string;
      totalAmount?: string;
      maxWin?: string;
      value?: string;
      points?: string;
    };
  }[];
  wagerX: number;
  payoutX: number;
  activationRules: {
    currency: string;
    type: string;
    config: {
      min?: string;
    };
  }[];
}
export interface PlayerItem {
  id?: number;
  roleId?: number;
  role?: {
    id?: number;
    title?: string;
  };
  phone?: string;
  balanceReal?: number;
  balanceBonus?: number;
  locale?: string;
  affId?: string;
  affiliateId?: number | string;
  email?: string;
  country?: string;
  currency?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  birthday?: string;
  roleName?: string;
  verificationStatus?: string;
  riskStatus?: string | number;
  riskStatusChanged?: boolean;
  riskScore?: {
    value: number | null;
    type: "low" | "medium" | "high" | "none";
  };
  category?: string;
  registeredAt?: string;
  isOnline?: boolean;
  blockUser?: false;
  blockDeposit?: false;
  blockBet?: false;
  publicId?: string;
  city?: string;
  timezone?: string;
  middleName?: string;
  nickname?: string;
  postalCode?: string;
  gender?: string;
  address?: string;
  state?: string;
  street?: string;
  house?: string;
  apartment?: string;
  nationality?: string;
  lastIp?: string;
  refferal?: string;
  updatedAt?: string;
  emailSubscribedAt?: string;
  emailUnsubscribedAt?: string;
  kycConfirmed?: boolean;
  isTest?: boolean;
  categoryId?: number;
  balanceType?: string;
  rankingPoints?: number;
  coins?: number;
  twoFaEnabled?: boolean;
}

export interface UpdatePlayerKycStatusPayload {
  userId: number;
  kycStatus: ManualKycStatus;
  reason: string;
}

export interface PlayerCasinoBetItem {
  id: number;
  action: string;
  type: string;
  amount: number;
  balanceType: string;
  balanceReal: number;
  balanceBonus: number;
  currency: string;
  sessionId: string | null;
  transactionId: string | null;
  roundId: string | null;
  gameTitle: string | null;
  gameProvider: string | null;
  userDevice: string | null;
  created: string;
}

export interface CasinoBetsFilters {
  createdFrom: string;
  createdTo: string;
  sessionId: string;
  transactionId: string;
  roundId: string;
  action: string;
  balanceType: string;
  gameTitle: string;
  gameProvider: string;
  amountMin: string;
  amountMax: string;
}
export interface PlayerSportBetItem {
  id: number;
  action: string | null;
  balanceType: string | null;
  amount: number | null;
  currency: string | null;
  created: string | null;
  bid: string | null;
  internalTransactionId: string | null;
  sessionId: string | null;
  betType: string | null;
  odds: number | null;
  updated: string | null;
  eventId: string | null;
  eventName: string | null;
  sport: string | null;
  category: string | null;
  competition: string | null;
  market: string | null;
  outcome: string | null;
  betslipStatus: string | null;
  outcomes: Array<{
    itemUuid?: string | null;
    market?: string | null;
    outcome?: string | null;
    odds?: number | null;
    event?: { name?: string | null; sport?: string | null } | null;
  }>;
}

export interface SportBetsFilters {
  betslipStatus: string;
  createdFrom: string;
  createdTo: string;
  transactionId: string;
  itemUuid: string;
  bid: string;
  sport: string;
  action: string;
  balanceType: string;
  eventName: string;
  amountMin: string;
  amountMax: string;
}

export interface PlayerDepositItem extends PaymentLifecycleView {
  id: number;
  amountAttempt: number;
  amountPaid: number;
  fee: number;
  status: string;
  statusId: number;
  currency: string;
  paymentMethod: string;
  paymentProviderImage: string;
  paymentProviderTitle: string;
  created: string;
}
export interface PlayerWagering {
  bucketId: string;
  sourceType: string;
  sourceId: string;
  operationId: string;
  status: string;
  ledgerCurrency: string;
  ledgerCurrencyScale: number;
  ledgerAmountMinor: string;
  wagerCurrency: string;
  wagerCurrencyScale: number;
  wagerAmountMinor: string;
  multiplier: string;
  requiredWagerMinor: string;
  completedWagerMinor: string;
  remainingWagerMinor: string;
  progressPercent: string;
  depositProcessedAt: string;
  bucketCreatedAt: string;
  unlockedAt: any;
}

export interface PlayerWithdrawalItem extends PaymentLifecycleView {
  origin?: "user" | "backoffice_manual";
  id: number;
  amount: number;
  fee: number;
  currency: string;
  paymentMethod: string | null;
  status: string;
  processingType: string;
  created: string;
  paymentProviderImage: string;
  paymentProviderTitle: string;
  destination?: string;
  destinationType?: string;
  externalRef?: string;
  operatorDeclineReason?: string | null;
}
export interface PlayerPaymentInfo {
  depositSum: number;
  depositCount: number;
  depositSucceed: number;
  depositFailed: number;
  depositPending: number;
  depositAttempt: number;
  withdrawalSum: number;
  withdrawalCount: number;
  withdrawalSucceed: number;
  withdrawalFailed: number;
  withdrawalPending: number;
  withdrawalAttempt: number;
}

export interface PlayerRiskItem {
  id: number;
  title: string;
  description: string;
}
export interface PlayerNotifItem {
  title: string;
  content: string;
  created: string;
  isChecked: boolean;
}
export interface PlayerNoteItem {
  id: number;
  content: string;
  created: string;
}
export interface PlayerActivitiesItem {
  type: string;
  action: string;
  message: string;
  created: string;
  hash: string;
}

export interface PlayerRiskProfile {
  riskLevel?: string;
  reason?: string;
  source?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CalculatedRestrictionLimit {
  amountMinor: number;
  currency: string;
  currencyScale: number;
}

export interface CalculatedRestrictionItem {
  restrictionType: string;
  allowed: boolean;
  source: string;
  reason?: string;
  limit?: CalculatedRestrictionLimit;
}

export interface PlayerCalculatedRestrictions {
  userId: number;
  riskLevel: string;
  items: CalculatedRestrictionItem[];
}

export interface ManualRestrictionItem {
  userId: number;
  restrictionType: string;
  isBlocked: boolean;
  reason?: string;
  source?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PlayerManualRestrictions {
  userId: number;
  items: ManualRestrictionItem[];
}

export interface RestrictionRow {
  restrictionType: string;
  restriction: string;
  isBlocked: boolean;
  reason: string;
  source: string;
  updatedAt: string;
}

interface PlayersState {
  list: PlayerItem[];
  riskList: PlayerRiskItem[];
  casinoBetsList: PlayerCasinoBetItem[];
  sportBetsList: PlayerSportBetItem[];
  notifsList: PlayerNotifItem[];
  depositsList: PlayerDepositItem[];
  withdrawalsList: PlayerWithdrawalItem[];
  compensationsList: CompensationItem[];
  confiscationsList: ConfiscationItem[];
  notesList: PlayerNoteItem[];
  activitiesList: PlayerActivitiesItem[];
  statusesList: PaymentStatusOption[];
  withdrawalStatusesList: PaymentStatusOption[];
  paymentInfo?: PlayerPaymentInfo;
  restrictionsInfo?: UserRestrictions;
  isRestrictionsFetched: boolean;
  activePage: number;
  totalPages: number;
  totalItems: number;
  activePageNotes: number;
  totalPagesNotes: number;
  totalItemsNotes: number;
  isRiskFetched: boolean;
  isListFetched: boolean;
  isStatusesFetched: boolean;
  isWithdrawalStatusesFetched: boolean;
  isNotesFetched: boolean;
  isRiskProfileFetched: boolean;
  activePageBets: number;
  totalPagesBets: number;
  totalItemsBets: number;
  isBetsFetched: boolean;
  betsError: string | null;
  isItemFetched: boolean;
  selectedItems: number[];
  payload?: PlayerItem;
  initialPayload?: PlayerItem;
  activePageNotifs: number;
  totalPagesNotifs: number;
  totalItemsNotifs: number;
  isNotifsFetched: boolean;
  activePageDeposits: number;
  activePageWithdrawals: number;
  activePageCompensations: number;
  activePageConfiscations: number;
  totalPagesDeposits: number;
  totalPagesWithdrawals: number;
  totalPagesCompensations: number;
  totalPagesConfiscations: number;
  totalItemsWithdrawals: number;
  totalItemsDeposits: number;
  totalItemsCompensations: number;
  totalItemsConfiscations: number;
  isDepositsFetched: boolean;
  isWithdrawalsFetched: boolean;
  isCompensationsFetched: boolean;
  isConfiscationsFetched: boolean;
  activePageActivities: number;
  totalPagesActivities: number;
  totalItemsActivities: number;
  isActivitiesFetched: boolean;
  isItemFetching: boolean;
  processingItemId?: PlayerItem["id"];
  riskProfile?: PlayerRiskProfile;
  initialRiskProfile?: PlayerRiskProfile;
  calculatedRestrictions?: PlayerCalculatedRestrictions;
  isCalculatedFetched: boolean;
  riskRestrictions?: PlayerManualRestrictions;
  isRiskRestrictionsFetched: boolean;
  isWageringFetched: boolean;
  wagering: PlayerWagering[];
  activePageWagering: number;
  totalPagesWareging: number;
  totalItemsWagering: number;
}
interface GetPlayerFinanceListPayload {
  userId: number;
  limit: number;
  offset: number;
}

interface GetListPayload {
  limit: number;
  offset: number;
  publicId?: string | string[];
  countries?: string[];
  currencies?: string[];
  riskStatuses?: number[];
  roleIds?: number[];
  categories?: number[];
  userIds?: number[];
  email?: string;
  registrationFrom?: string;
  registrationTo?: string;
  verificationStatuses?: number[];
  order: {
    id: string;
    email: string;
    country: string;
    currency: string;
    registeredAt: string;
    balanceReal: string;
    balanceBonus: string;
    category: string;
    riskStatus: string;
    verificationStatus: string;
  };
}
interface GetBetsListPayload {
  limit: number;
  offset: number;
  id: number;
}
interface GetCasinoBetsListPayload extends GetBetsListPayload {
  filters?: Partial<CasinoBetsFilters>;
}
interface GetSportBetsListPayload extends GetBetsListPayload {
  filters?: Partial<SportBetsFilters>;
}

interface CasinoBetsResponse {
  items: PlayerCasinoBetItem[];
  totalItems: number;
  totalPages: number;
}
interface SportBetsResponse {
  items: PlayerSportBetItem[];
  totalItems: number;
  totalPages: number;
}
interface CreateListPayload {
  content: string;
  userId: number;
}
interface GetPlayerWageringPayload {
  limit: number;
  offset: number;
  id: number;
}

interface PlayerWageringResponse {
  items: PlayerWagering[];
  totalItems: number;
  totalPages: number;
}

const initialState: PlayersState = {
  list: [],
  casinoBetsList: [],
  sportBetsList: [],
  riskList: [],
  notifsList: [],
  depositsList: [],
  withdrawalsList: [],
  compensationsList: [],
  confiscationsList: [],
  statusesList: [],
  withdrawalStatusesList: [],
  notesList: [],
  activitiesList: [],
  isListFetched: false,
  isItemFetched: false,
  isRestrictionsFetched: false,
  isRiskFetched: false,
  isNotifsFetched: false,
  isDepositsFetched: false,
  isWithdrawalsFetched: false,
  isCompensationsFetched: false,
  isConfiscationsFetched: false,
  isStatusesFetched: false,
  isWithdrawalStatusesFetched: false,
  isNotesFetched: false,
  isRiskProfileFetched: false,
  isActivitiesFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  activePageNotes: 1,
  totalItemsNotes: 0,
  totalPagesNotes: 0,
  isBetsFetched: false,
  betsError: null,
  activePageBets: 1,
  totalItemsBets: 0,
  totalPagesBets: 0,
  selectedItems: [],
  activePageNotifs: 1,
  totalItemsNotifs: 0,
  totalPagesNotifs: 0,
  activePageDeposits: 1,
  activePageWithdrawals: 1,
  activePageCompensations: 1,
  activePageConfiscations: 1,
  totalItemsDeposits: 0,
  totalItemsWithdrawals: 0,
  totalItemsCompensations: 0,
  totalItemsConfiscations: 0,
  totalPagesDeposits: 0,
  totalPagesWithdrawals: 0,
  totalPagesCompensations: 0,
  totalPagesConfiscations: 0,
  activePageActivities: 1,
  totalItemsActivities: 0,
  totalPagesActivities: 0,
  isItemFetching: false,
  riskProfile: undefined,
  calculatedRestrictions: undefined,
  isCalculatedFetched: false,
  riskRestrictions: undefined,
  isRiskRestrictionsFetched: false,
  isWageringFetched: false,
  wagering: [],
  activePageWagering: 0,
  totalPagesWareging: 0,
  totalItemsWagering: 0,
};

export const getRestrictions = createAsyncThunk(
  "players/restrictions",
  async (id: string) => {
    try {
      const result = await instance.get(
        `${apiRoutes.playerRestrictions}/${id}`,
      );
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getActivitiesList = createAsyncThunk(
  "players/activities",
  async (data: GetBetsListPayload) => {
    try {
      const result = await instance.get(
        `${apiRoutes.playerActivitiesList}/${data.id}?limit=${data.limit}&offset=${data.offset}`,
      );
      return result.data;
    } catch (error) {
      return [];
    }
  },
);

export const createNote = createAsyncThunk(
  "players/note-create",
  async (data: CreateListPayload, { rejectWithValue }) => {
    try {
      await instance.post(`${apiRoutes.playerNoticeCreate}`, data);
      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getNotesList = createAsyncThunk(
  "players/notes",
  async (data: GetBetsListPayload) => {
    try {
      const result = await instance.get(
        `${apiRoutes.playerNoticesList}?limit=${data.limit}&offset=${data.offset}&userId=${data.id}`,
      );
      return result.data;
    } catch (error) {
      return [];
    }
  },
);
export const getRiskList = createAsyncThunk("players/risk", async () => {
  try {
    const result = await instance.get(
      `${apiRoutes.playerRiskList}?limit=500&offset=0`,
    );
    return result.data.items;
  } catch (error) {
    return [];
  }
});
export const getDepositsList = createAsyncThunk(
  "players/deposits-list",
  async (data: GetBetsListPayload) => {
    try {
      const result = await instance.get(
        `${apiRoutes.playerDepositsList}/${data.id}?limit=${data.limit}&offset=${data.offset}&canonical=true`,
      );
      return result.data;
    } catch (error) {
      return [];
    }
  },
);
export const getWithdrawalsList = createAsyncThunk(
  "players/withdrawals-list",
  async (data: GetBetsListPayload) => {
    try {
      const result = await instance.get(
        `${apiRoutes.playerWithdrawalsList}/${data.id}?limit=${data.limit}&offset=${data.offset}`,
      );
      return result.data;
    } catch (error) {
      return [];
    }
  },
);
export const getPlayerCompensationsList = createAsyncThunk(
  "players/compensations-list",
  async (data: GetPlayerFinanceListPayload) => {
    try {
      const result = await instance.post(apiRoutes.paymentCompensationsList, {
        limit: data.limit,
        offset: data.offset,
        userIds: [data.userId],
        order: { id: "desc" },
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getPlayerConfiscationsList = createAsyncThunk(
  "players/confiscations-list",
  async (data: GetPlayerFinanceListPayload) => {
    try {
      const result = await instance.post(apiRoutes.paymentConfiscationsList, {
        limit: data.limit,
        offset: data.offset,
        userIds: [data.userId],
        order: { id: "desc" },
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getDepositStatusesList = createAsyncThunk(
  "players/deposits-statuses",
  async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.paymentDepositStatuses}?limit=500&offset=0&canonical=true`,
      );
      return result.data.items;
    } catch (error) {
      return [];
    }
  },
);

export const getWithdrawalStatusesList = createAsyncThunk(
  "players/withdrawal-statuses",
  async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.paymentWithdrawalStatuses}?limit=500&offset=0&canonical=true`,
      );
      return result.data.items;
    } catch (error) {
      return [];
    }
  },
);
export const getPaymentInfo = createAsyncThunk(
  "players/payment-info",
  async (id: string) => {
    try {
      const result = await instance.get(`${apiRoutes.playerPaymentInfo}/${id}`);
      return result.data;
    } catch (error) {
      return [];
    }
  },
);
export const updatePlayer = createAsyncThunk(
  "players/update",
  async (data: PlayerItem, { rejectWithValue, getState }) => {
    try {
      const state = getState() as PlayersState | { players: PlayersState };
      const initialPayload =
        "players" in state
          ? state.players.initialPayload
          : state.initialPayload;
      const textFields: Array<keyof PlayerItem> = [
        "email",
        "phone",
        "firstName",
        "lastName",
        "birthday",
        "nickname",
        "middleName",
        "state",
        "city",
        "street",
        "house",
        "apartment",
        "postalCode",
        "address",
      ];
      const editableFields: Array<keyof PlayerItem> = [
        ...textFields,
        "country",
        "blockUser",
        "blockDeposit",
        "blockBet",
        "roleId",
        "isTest",
      ];
      const changes = getChangedFields(
        initialPayload,
        data,
        editableFields,
        Object.fromEntries([
          ...textFields.map((field) => [field, normalizeOptionalText]),
          ["blockUser", (value: unknown) => (value ? 1 : 0)],
          ["blockDeposit", (value: unknown) => (value ? 1 : 0)],
          ["blockBet", (value: unknown) => (value ? 1 : 0)],
          ["isTest", (value: unknown) => Boolean(value)],
        ]) as Partial<Record<keyof PlayerItem, typeof normalizeOptionalText>>,
      );
      const numericRiskStatusId =
        data.riskStatusChanged && typeof data.riskStatus === "number"
          ? data.riskStatus
          : data.riskStatusChanged &&
              typeof data.riskStatus === "string" &&
              /^\d+$/.test(data.riskStatus.trim())
            ? Number(data.riskStatus)
            : undefined;

      const body: Record<string, unknown> = {
        ...changes,
        ...(data.riskStatusChanged && numericRiskStatusId !== undefined
          ? { riskStatusId: numericRiskStatusId }
          : {}),
      };

      await instance.put(`${apiRoutes.playerUpdate}/${data.id}`, body);

      return true;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const updatePlayerKycStatus = createAsyncThunk<
  unknown,
  UpdatePlayerKycStatusPayload,
  { rejectValue: unknown }
>("players/update-kyc-status", async (data, { rejectWithValue }) => {
  try {
    const result = await instance.put(apiRoutes.playerKycProfile(data.userId), {
      kyc_status: data.kycStatus,
      reason: data.reason,
    });

    return result.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});
export const getPlayersList = createAsyncThunk(
  "players/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.playersList}`, data);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getPlayerItem = createAsyncThunk(
  "players/item",
  async (id: number) => {
    try {
      const result = await instance.get(`${apiRoutes.playerItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getPlayerCasinoBetsList = createAsyncThunk<
  CasinoBetsResponse,
  GetCasinoBetsListPayload,
  { rejectValue: string }
>("players/casino-history", async (data, { rejectWithValue }) => {
  try {
    const result = await instance.get(
      `${apiRoutes.playerCasinoBetsList}/${data.id}`,
      {
        params: {
          limit: data.limit,
          offset: data.offset,
          ...data.filters,
        },
      },
    );
    return result.data;
  } catch (error) {
    return rejectWithValue("failedToLoadBets");
  }
});
export const getPlayerSportBetsList = createAsyncThunk<
  SportBetsResponse,
  GetSportBetsListPayload,
  { rejectValue: string }
>("players/sport-history", async (data, { rejectWithValue }) => {
  try {
    const result = await instance.get(
      `${apiRoutes.playerSportBetsList}/${data.id}`,
      {
        params: {
          limit: data.limit,
          offset: data.offset,
          ...data.filters,
        },
      },
    );
    return result.data;
  } catch (error) {
    return rejectWithValue("failedToLoadBets");
  }
});

export const getPlayerNotifsList = createAsyncThunk(
  "players/notifications",
  async (data: GetBetsListPayload) => {
    try {
      const result = await instance.get(
        `${apiRoutes.playerNotifsList}/${data.id}?limit=${data.limit}&offset=${data.offset}`,
      );
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const updatePlayerRestrictions = createAsyncThunk(
  "players/update-restrictions",
  async (data: UserRestrictions & { id: string }, { rejectWithValue }) => {
    const { id, ...rest } = data;

    try {
      await instance.put(`${apiRoutes.playerRestrictionsSet}/${id}`, rest);

      return true;
    } catch (error: any) {
      return rejectWithValue(
        error?.data?.error ??
          error?.data ?? {
            code: "InternalError",
            fieldErrors: [],
            globalErrors: [{ code: "unknownError" }],
          },
      );
    }
  },
);

export const getPlayerWageringList = createAsyncThunk<
  PlayerWageringResponse,
  GetPlayerWageringPayload
>("players/getPlayerWagering", async (data) => {
  try {
    const result = await instance.get(
      `${apiRoutes.playerWagering}/${data.id}?limit=${data.limit}&offset=${data.offset}`,
    );

    return result.data;
  } catch (error) {
    return undefined;
  }
});

export const getPlayerRiskProfile = createAsyncThunk(
  "players/risk-profile",
  async (id: string) => {
    try {
      const result = await instance.get(`${apiRoutes.playerRiskProfile}/${id}`);

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const updatePlayerRiskProfile = createAsyncThunk<
  boolean,
  PlayerRiskProfile & { id: string },
  { rejectValue: ApiErrorPayload & { status?: number } }
>(
  "players/update-risk-profile",
  async (data, { rejectWithValue, getState }) => {
    const { id, ...rest } = data;
    const state = getState() as PlayersState | { players: PlayersState };
    const initialRiskProfile =
      "players" in state
        ? state.players.initialRiskProfile
        : state.initialRiskProfile;
    const changes = getChangedFields(
      initialRiskProfile,
      rest,
      ["riskLevel", "reason"],
      { reason: normalizeOptionalText },
    );

    try {
      await instance.put(`${apiRoutes.playerRiskProfileSet}/${id}`, changes);

      return true;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError?.code) {
        return rejectWithValue({ ...apiError, status: error?.status });
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
        status: error?.status,
      });
    }
  },
);

export const getPlayerRiskCalculated = createAsyncThunk(
  "players/calculated-restrictions",
  async ({ id, currency }: { id: string; currency?: string }) => {
    try {
      const query = currency ? `?currency=${currency}` : "";

      const result = await instance.get(
        `${apiRoutes.playerRiskCalculated}/${id}${query}`,
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getPlayerRiskRestrictions = createAsyncThunk(
  "players/manual-restrictions",
  async (id: string) => {
    try {
      const result = await instance.get(
        `${apiRoutes.playerRiskRestrictions}/${id}`,
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const updatePlayerRiskRestriction = createAsyncThunk<
  boolean,
  {
    id: string;
    items: {
      restrictionType: string;
      isBlocked: number;
      reason: string;
    }[];
  },
  { rejectValue: ApiErrorPayload & { status?: number } }
>("players/update-manual-restriction", async (data, { rejectWithValue }) => {
  const { id, items } = data;

  try {
    await instance.put(`${apiRoutes.playerRiskRestrictions}/${id}/bunch`, {
      items,
    });

    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue({ ...apiError, status: error?.status });
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
      status: error?.status,
    });
  }
});

export const playersSlice = createSlice({
  name: "players",
  initialState,
  reducers: {
    handleUpdateFields: (
      state: PlayersState,
      action: PayloadAction<PlayerItem>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleInitializeRoleId: (
      state: PlayersState,
      action: PayloadAction<number>,
    ) => {
      if (state.payload?.roleId) return;

      state.payload = { ...state.payload, roleId: action.payload };
      state.initialPayload = {
        ...state.initialPayload,
        roleId: action.payload,
      };
    },
    handleUpdateFieldsRestrictions: (
      state: PlayersState,
      action: PayloadAction<UserRestrictions | undefined>,
    ) => {
      state.restrictionsInfo = action.payload
        ? { ...state.restrictionsInfo, ...action.payload }
        : undefined;
      if (!action.payload) state.isRestrictionsFetched = false;
    },
    handleClearFields: (state: PlayersState) => {
      state.payload = undefined;
      state.initialPayload = undefined;
      state.isItemFetched = false;
    },
    handlePaginateNotes: (
      state: PlayersState,
      action: PayloadAction<number>,
    ) => {
      state.activePageNotes = action.payload;
      state.isNotesFetched = false;
    },
    handlePaginate: (state: PlayersState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleDepositsPaginate: (
      state: PlayersState,
      action: PayloadAction<number>,
    ) => {
      state.activePageDeposits = action.payload;
      state.isDepositsFetched = false;
    },
    handleWithdrawalsPaginate: (
      state: PlayersState,
      action: PayloadAction<number>,
    ) => {
      state.activePageWithdrawals = action.payload;
      state.isWithdrawalsFetched = false;
    },
    handleCompensationsPaginate: (
      state: PlayersState,
      action: PayloadAction<number>,
    ) => {
      state.activePageCompensations = action.payload;
      state.isCompensationsFetched = false;
    },
    handleConfiscationsPaginate: (
      state: PlayersState,
      action: PayloadAction<number>,
    ) => {
      state.activePageConfiscations = action.payload;
      state.isConfiscationsFetched = false;
    },
    handleBetsPaginate: (
      state: PlayersState,
      action: PayloadAction<number>,
    ) => {
      state.activePageBets = action.payload;
      state.isBetsFetched = false;
    },
    handleNotifsPaginate: (
      state: PlayersState,
      action: PayloadAction<number>,
    ) => {
      state.activePageNotifs = action.payload;
      state.isNotifsFetched = false;
    },
    handleActivitiesPaginate: (
      state: PlayersState,
      action: PayloadAction<number>,
    ) => {
      state.activePageActivities = action.payload;
      state.isActivitiesFetched = false;
    },

    handleNotifsClear: (state: PlayersState) => {
      state.activePageNotifs = 1;
      state.notifsList = [];
      state.isNotifsFetched = false;
    },
    handleDepositsClear: (state: PlayersState) => {
      state.activePageDeposits = 1;
      state.depositsList = [];
      state.isDepositsFetched = false;
    },
    handleWithdrawalsClear: (state: PlayersState) => {
      state.activePageWithdrawals = 1;
      state.withdrawalsList = [];
      state.isWithdrawalsFetched = false;
    },
    handleCompensationsClear: (state: PlayersState) => {
      state.activePageCompensations = 1;
      state.compensationsList = [];
      state.isCompensationsFetched = false;
    },
    handleConfiscationsClear: (state: PlayersState) => {
      state.activePageConfiscations = 1;
      state.confiscationsList = [];
      state.isConfiscationsFetched = false;
    },
    handleNotesClear: (state: PlayersState) => {
      state.activePageNotes = 1;
      state.notesList = [];
      state.isNotesFetched = false;
    },
    handleActivitiesClear: (state: PlayersState) => {
      state.activePageActivities = 1;
      state.activitiesList = [];
      state.isActivitiesFetched = false;
    },
    handleInfoClear: (state: PlayersState) => {
      state.payload = undefined;
      state.isItemFetched = false;
      state.riskProfile = undefined;
      state.initialRiskProfile = undefined;
    },
    handleSelectItem: (state: PlayersState, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: PlayersState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
    handleUpdateFieldsRiskProfile: (
      state: PlayersState,
      action: PayloadAction<PlayerRiskProfile | undefined>,
    ) => {
      state.riskProfile = action.payload
        ? { ...state.riskProfile, ...action.payload }
        : undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getActivitiesList.fulfilled, (state, action) => {
      state.isActivitiesFetched = true;
      state.activitiesList = action.payload?.items ?? [];
      state.totalItemsActivities = action.payload?.totalItems ?? 1;
      state.totalPagesActivities = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createNote.fulfilled, (state) => {
      state.activePageNotes = 1;
      state.isNotesFetched = false;
    });
    builder.addCase(getNotesList.fulfilled, (state, action) => {
      state.isNotesFetched = true;
      state.notesList = action.payload?.items ?? [];
      state.totalItemsNotes = action.payload?.totalItems ?? 1;
      state.totalPagesNotes = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getPlayerCasinoBetsList.fulfilled, (state, action) => {
      state.isBetsFetched = true;
      state.betsError = null;
      state.casinoBetsList = action.payload.items;
      state.totalItemsBets = action.payload.totalItems;
      state.totalPagesBets = action.payload.totalPages;
    });
    builder.addCase(getPlayerCasinoBetsList.pending, (state) => {
      state.isBetsFetched = false;
      state.betsError = null;
    });
    builder.addCase(getPlayerCasinoBetsList.rejected, (state, action) => {
      state.isBetsFetched = true;
      state.betsError = action.payload ?? "failedToLoadBets";
      state.casinoBetsList = [];
      state.totalItemsBets = 0;
      state.totalPagesBets = 0;
    });
    builder.addCase(getPlayerSportBetsList.fulfilled, (state, action) => {
      state.isBetsFetched = true;
      state.betsError = null;
      state.sportBetsList = action.payload.items;
      state.totalItemsBets = action.payload.totalItems;
      state.totalPagesBets = action.payload.totalPages;
    });
    builder.addCase(getPlayerSportBetsList.pending, (state) => {
      state.isBetsFetched = false;
      state.betsError = null;
    });
    builder.addCase(getPlayerSportBetsList.rejected, (state, action) => {
      state.isBetsFetched = true;
      state.betsError = action.payload ?? "failedToLoadBets";
      state.sportBetsList = [];
      state.totalItemsBets = 0;
      state.totalPagesBets = 0;
    });
    builder.addCase(getPlayersList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getPlayerNotifsList.fulfilled, (state, action) => {
      state.isNotifsFetched = true;
      state.notifsList = action.payload?.items ?? [];
      state.totalItemsNotifs = action.payload?.totalItems ?? 1;
      state.totalPagesNotifs = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getPlayerItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      const payload = action.payload
        ? {
            ...action.payload,
            isTest:
              action.payload.isTest === true ||
              action.payload.isTest === 1 ||
              action.payload.isTest === "1",
          }
        : undefined;
      state.payload = payload;
      state.initialPayload = payload ? { ...payload } : undefined;
    });
    builder.addCase(getRiskList.fulfilled, (state, action) => {
      state.isRiskFetched = true;
      state.riskList = action.payload;
    });
    builder.addCase(updatePlayer.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updatePlayer.fulfilled, (state, action) => {
      const listFields = [
        "email",
        "isOnline",
        "isTest",
        "riskScore",
        "country",
        "currency",
        "balanceReal",
        "balanceBonus",
        "verificationStatus",
        "category",
        "registeredAt",
        "publicId",
        "nickname",
        "affiliateId",
      ] as const;
      const listUpdates = Object.fromEntries(
        listFields
          .filter((field) => action.meta.arg[field] !== undefined)
          .map((field) => [field, action.meta.arg[field]]),
      );
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? {
              ...item,
              ...listUpdates,
            }
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
      state.initialPayload = state.payload ? { ...state.payload } : undefined;
    });
    builder.addCase(updatePlayer.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getDepositStatusesList.fulfilled, (state, action) => {
      state.statusesList = action.payload.map((el: any) => {
        return {
          value: el.code,
          statusId: el.statusId,
          label: el.title,
        };
      });
      state.isStatusesFetched = true;
    });
    builder.addCase(getWithdrawalStatusesList.fulfilled, (state, action) => {
      state.withdrawalStatusesList = action.payload.map((el: any) => ({
        value: el.code,
        statusId: el.statusId,
        label: el.title,
      }));
      state.isWithdrawalStatusesFetched = true;
    });
    builder.addCase(getDepositsList.fulfilled, (state, action) => {
      state.depositsList = action.payload?.items ?? [];
      state.isDepositsFetched = true;
      state.totalItemsDeposits = action.payload?.totalItems ?? 1;
      state.totalPagesDeposits = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getWithdrawalsList.fulfilled, (state, action) => {
      state.withdrawalsList = action.payload?.items ?? [];
      state.isWithdrawalsFetched = true;
      state.totalItemsWithdrawals = action.payload?.totalItems ?? 1;
      state.totalPagesWithdrawals = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getPlayerCompensationsList.fulfilled, (state, action) => {
      state.compensationsList = action.payload?.items ?? [];
      state.isCompensationsFetched = true;
      state.totalItemsCompensations = action.payload?.totalItems ?? 0;
      state.totalPagesCompensations = action.payload?.totalPages ?? 0;
    });
    builder.addCase(getPlayerConfiscationsList.fulfilled, (state, action) => {
      state.confiscationsList = action.payload?.items ?? [];
      state.isConfiscationsFetched = true;
      state.totalItemsConfiscations = action.payload?.totalItems ?? 0;
      state.totalPagesConfiscations = action.payload?.totalPages ?? 0;
    });
    builder.addCase(getPaymentInfo.fulfilled, (state, action) => {
      state.paymentInfo = action.payload;
    });
    builder.addCase(getRestrictions.fulfilled, (state, action) => {
      state.restrictionsInfo = action.payload;
      state.isRestrictionsFetched = true;
    });
    builder.addCase(getRestrictions.pending, (state) => {
      state.isRestrictionsFetched = false;
    });
    builder.addCase(updatePlayerRestrictions.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(updatePlayerRestrictions.fulfilled, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updatePlayerRestrictions.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(getPlayerRiskProfile.pending, (state) => {
      state.isRiskProfileFetched = false;
    });
    builder.addCase(getPlayerRiskProfile.fulfilled, (state, action) => {
      state.riskProfile = action.payload;
      state.initialRiskProfile = action.payload
        ? { ...action.payload }
        : undefined;
      state.isRiskProfileFetched = true;
    });
    builder.addCase(getPlayerRiskProfile.rejected, (state) => {
      state.isRiskProfileFetched = true;
    });
    builder.addCase(updatePlayerRiskProfile.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(getPlayerWageringList.pending, (state) => {
      state.isWageringFetched = false;
    });
    builder.addCase(getPlayerWageringList.fulfilled, (state, action) => {
      state.isWageringFetched = true;
      state.wagering = action.payload.items;
      state.totalItemsWagering = action.payload.totalItems;
      state.totalPagesWareging = action.payload.totalPages;
    });
    builder.addCase(getPlayerWageringList.rejected, (state) => {
      state.isWageringFetched = false;
    });
    builder.addCase(updatePlayerRiskProfile.fulfilled, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(updatePlayerRiskProfile.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(getPlayerRiskCalculated.pending, (state) => {
      state.isCalculatedFetched = false;
    });

    builder.addCase(getPlayerRiskCalculated.fulfilled, (state, action) => {
      state.calculatedRestrictions = action.payload;
      state.isCalculatedFetched = true;
    });

    builder.addCase(getPlayerRiskCalculated.rejected, (state) => {
      state.calculatedRestrictions = undefined;
      state.isCalculatedFetched = true;
    });
    builder.addCase(getPlayerRiskRestrictions.pending, (state) => {
      state.isRiskRestrictionsFetched = false;
    });

    builder.addCase(getPlayerRiskRestrictions.fulfilled, (state, action) => {
      state.riskRestrictions = action.payload;
      state.isRiskRestrictionsFetched = true;
    });

    builder.addCase(getPlayerRiskRestrictions.rejected, (state) => {
      state.riskRestrictions = undefined;
      state.isRiskRestrictionsFetched = true;
    });

    builder.addCase(updatePlayerRiskRestriction.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(updatePlayerRiskRestriction.fulfilled, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(updatePlayerRiskRestriction.rejected, (state) => {
      state.isItemFetching = false;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleInitializeRoleId,
  handleClearFields,
  handleBetsPaginate,
  handleInfoClear,
  handleNotifsPaginate,
  handleNotifsClear,
  handleDepositsClear,
  handleDepositsPaginate,
  handlePaginateNotes,
  handleNotesClear,
  handleActivitiesClear,
  handleActivitiesPaginate,
  handleUpdateFieldsRestrictions,
  handleWithdrawalsPaginate,
  handleWithdrawalsClear,
  handleCompensationsPaginate,
  handleCompensationsClear,
  handleConfiscationsPaginate,
  handleConfiscationsClear,
  handleUpdateFieldsRiskProfile,
} = playersSlice.actions;
